// German Payroll Engine - implements the abstract PayrollEngine for German compliance

import { supabase } from '@/integrations/supabase/client';
import { Employee } from '@/types';
import { PayrollEngine } from '../core/PayrollEngine';
import {
  CountryPayrollConstants,
  PayrollInputData,
  GrossPayComponents,
  InsuranceCalculation,
  TaxCalculation,
  DeductionCalculation,
  ValidationError,
  CalculationError
} from '../core/types';

// German-specific types
export type GermanContractType = 'VOLLZEIT' | 'TEILZEIT' | 'MINIJOB' | 'MIDIJOB';
export type GermanTaxClass = 1 | 2 | 3 | 4 | 5 | 6;

interface GermanPayrollConstants {
  year: number;
  country_code: string;
  
  // Minimum wage
  minimum_wage_hourly: number;
  minimum_wage_monthly: number;
  
  // Tax system
  basic_tax_allowance: number; // Grundfreibetrag
  
  // Tax brackets (2024 rates)
  tax_rate_14_percent_limit: number; // €11,784
  tax_rate_24_percent_limit: number; // €66,760  
  tax_rate_42_percent_limit: number; // €277,826
  tax_rate_45_percent: number; // Above €277,826
  
  // Social insurance rates
  pension_rate_employee: number; // 9.3%
  pension_rate_employer: number; // 9.3% 
  unemployment_rate_employee: number; // 1.3%
  unemployment_rate_employer: number; // 1.3%
  
  // Health insurance (average rate)
  health_rate_employee: number; // 7.3%
  health_rate_employer: number; // 7.3%
  health_additional_rate_employee: number; // ~1.7% average
  
  // Care insurance (Pflegeversicherung)
  care_rate_employee: number; // 1.525%
  care_rate_employer: number; // 1.525%
  care_additional_rate_childless: number; // 0.6% for childless over 23
  
  // Assessment limits
  pension_assessment_limit_monthly: number; // €7,550 West, €7,450 East
  health_assessment_limit_monthly: number; // €5,175
  
  // Mini/Midijob thresholds
  minijob_threshold: number; // €520
  midijob_threshold: number; // €2,000
  
  // Church tax rates by state
  church_tax_rate_8: number; // 8% (most states)
  church_tax_rate_9: number; // 9% (Bavaria, Baden-Württemberg)
  
  // Overtime and special rates
  overtime_premium_rate: number; // Typically 25-50%
  night_work_premium_rate: number; // 25%
  sunday_premium_rate: number; // 50%
  holiday_premium_rate: number; // 100%
  
  // Other constants
  solidarity_surcharge_rate: number; // 5.5% on income tax
  solidarity_surcharge_threshold: number; // Exemption limit
}

/**
 * German Payroll Calculation Engine
 * Implements German-specific payroll calculations including taxes,
 * social insurance, and employment classifications (Minijob, Midijob, etc.)
 */
export class GermanPayrollEngine extends PayrollEngine {
  private employee!: Employee;
  private germanConstants!: GermanPayrollConstants;
  private year!: number;

  constructor(countryCode: string = 'DE', debugMode: boolean = false) {
    super(countryCode, debugMode);
  }

  /**
   * Load German payroll constants from database
   */
  protected async loadPayrollConstants(year: number): Promise<CountryPayrollConstants> {
    this.log(`Loading German payroll constants for year ${year}`);
    
    const { data, error } = await supabase
      .from('payroll_constants')
      .select('*')
      .eq('year', year)
      .eq('country_code', 'DE')
      .single();

    if (error || !data) {
      // Create default German constants if not found
      this.germanConstants = this.createDefaultGermanConstants(year);
      this.log('Using default German constants (database constants not found)');
    } else {
      this.germanConstants = data as GermanPayrollConstants;
    }
    
    this.year = year;

    // Convert to generic format
    return {
      countryCode: 'DE',
      year,
      currency: 'EUR',
      minimumWageHourly: this.germanConstants.minimum_wage_hourly,
      minimumWageMonthly: this.germanConstants.minimum_wage_monthly,
      minimumWageAnnual: this.germanConstants.minimum_wage_monthly * 12,
      standardHoursPerWeek: 40,
      maxHoursPerWeek: 48,
      maxHoursPerDay: 8,
      taxSystem: 'PROGRESSIVE',
      taxBrackets: [
        { min: 0, max: this.germanConstants.basic_tax_allowance, rate: 0 },
        { min: this.germanConstants.basic_tax_allowance, max: this.germanConstants.tax_rate_14_percent_limit, rate: 0.14 },
        { min: this.germanConstants.tax_rate_14_percent_limit, max: this.germanConstants.tax_rate_24_percent_limit, rate: 0.24 },
        { min: this.germanConstants.tax_rate_24_percent_limit, max: this.germanConstants.tax_rate_42_percent_limit, rate: 0.42 },
        { min: this.germanConstants.tax_rate_42_percent_limit, max: null, rate: 0.45 }
      ],
      personalAllowance: this.germanConstants.basic_tax_allowance,
      dependentAllowances: {
        child: 0 // Germany uses Kindergeld (child benefit) system, not tax allowances
      },
      socialInsuranceRates: {
        employee: {
          pension: this.germanConstants.pension_rate_employee,
          unemployment: this.germanConstants.unemployment_rate_employee,
          health: this.germanConstants.health_rate_employee + this.germanConstants.health_additional_rate_employee,
          care: this.germanConstants.care_rate_employee
        },
        employer: {
          pension: this.germanConstants.pension_rate_employer,
          unemployment: this.germanConstants.unemployment_rate_employer,
          health: this.germanConstants.health_rate_employer,
          care: this.germanConstants.care_rate_employer
        }
      },
      healthInsuranceRates: {
        employee: this.germanConstants.health_rate_employee + this.germanConstants.health_additional_rate_employee,
        employer: this.germanConstants.health_rate_employer
      },
      maxAssessmentBase: this.germanConstants.pension_assessment_limit_monthly,
      minAssessmentBase: 0,
      countrySpecificConstants: {
        contractTypes: ['VOLLZEIT', 'TEILZEIT', 'MINIJOB', 'MIDIJOB'],
        miniJobThreshold: this.germanConstants.minijob_threshold,
        midiJobThreshold: this.germanConstants.midijob_threshold,
        solidaritySurchargeRate: this.germanConstants.solidarity_surcharge_rate,
        churchTaxRate8: this.germanConstants.church_tax_rate_8,
        churchTaxRate9: this.germanConstants.church_tax_rate_9
      },
      validationRules: {
        mandatoryEmployeeFields: ['steuer_id', 'contract_type', 'tax_class'],
        employeeFieldValidators: {
          steuer_id: { type: 'custom', customValidator: 'validateGermanTaxId' },
          contract_type: { type: 'string', pattern: '^(VOLLZEIT|TEILZEIT|MINIJOB|MIDIJOB)$' },
          tax_class: { type: 'number', min: 1, max: 6 }
        },
        contractTypes: ['VOLLZEIT', 'TEILZEIT', 'MINIJOB', 'MIDIJOB'],
        payFrequencies: ['monthly']
      }
    };
  }

  /**
   * Calculate gross pay components for German employees
   */
  protected async calculateGrossComponents(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<GrossPayComponents> {
    this.log(`Calculating gross components for German employee ${employee.name}`);
    
    this.employee = employee;
    
    // Get hourly rate
    const hourlyRate = this.getHourlyRate(employee);
    this.log(`Using hourly rate: €${hourlyRate}/hour`);
    
    // Calculate base components
    const basePay = inputData.regularHours * hourlyRate;
    const overtimePay = inputData.overtimeHours * hourlyRate * (1 + this.germanConstants.overtime_premium_rate);
    
    // Special pay calculations (German-specific premiums)
    const nightPay = (inputData.specialHours.night || 0) * hourlyRate * this.germanConstants.night_work_premium_rate;
    const sundayPay = (inputData.specialHours.sunday || 0) * hourlyRate * this.germanConstants.sunday_premium_rate;
    const holidayPay = (inputData.specialHours.holiday || 0) * hourlyRate * this.germanConstants.holiday_premium_rate;
    
    // Absence pay
    const vacationPay = inputData.vacationDays * hourlyRate * 8; // 8 hours per day
    const sickPay = inputData.sickLeaveDays * hourlyRate * 8; // Continued pay for first 6 weeks
    
    const specialPay = {
      night: nightPay,
      sunday: sundayPay,
      holiday: holidayPay
    };
    
    const absencePay = {
      vacation: vacationPay,
      sickLeave: sickPay
    };
    
    const total = basePay + overtimePay + nightPay + sundayPay + holidayPay + 
                 vacationPay + sickPay + inputData.bonuses + inputData.otherIncome;
    
    this.log(`German gross components: Base €${basePay}, OT €${overtimePay}, Special €${nightPay + sundayPay + holidayPay}, Total €${total}`);
    
    return {
      basePay,
      overtimePay,
      specialPay,
      absencePay,
      bonuses: inputData.bonuses,
      otherIncome: inputData.otherIncome,
      total
    };
  }

  /**
   * Calculate German social insurance contributions
   */
  protected async calculateInsuranceContributions(
    employee: Employee,
    grossComponents: GrossPayComponents,
    inputData: PayrollInputData
  ): Promise<Record<string, InsuranceCalculation>> {
    this.log(`Calculating German insurance contributions`);
    
    const contractType = employee.contract_type as GermanContractType;
    const grossSalary = grossComponents.total;
    
    // Handle special cases for Minijob and Midijob
    if (contractType === 'MINIJOB') {
      return this.calculateMinijobInsurance(grossSalary);
    }
    
    if (contractType === 'MIDIJOB') {
      return this.calculateMidijobInsurance(grossSalary);
    }
    
    // Standard employee insurance calculation
    return this.calculateStandardInsurance(grossSalary);
  }

  /**
   * Calculate German income tax
   */
  protected async calculateTax(
    employee: Employee,
    grossComponents: GrossPayComponents,
    insuranceCalculations: Record<string, InsuranceCalculation>,
    inputData: PayrollInputData
  ): Promise<TaxCalculation> {
    this.log(`Calculating German tax`);
    
    const totalInsuranceEmployee = Object.values(insuranceCalculations)
      .reduce((sum, calc) => sum + calc.employeeTotal, 0);
    
    const taxBase = grossComponents.total - totalInsuranceEmployee;
    
    // Apply basic tax allowance (Grundfreibetrag)
    const taxableIncome = Math.max(0, taxBase - this.germanConstants.basic_tax_allowance);
    
    // Calculate income tax using German progressive tax brackets
    const incomeTax = this.calculateGermanIncomeTax(taxableIncome, employee.tax_class as GermanTaxClass || 1);
    
    // Calculate solidarity surcharge (Solidaritätszuschlag)
    const solidaritySurcharge = this.calculateSolidaritySurcharge(incomeTax);
    
    // Calculate church tax if applicable
    const churchTax = this.calculateChurchTax(incomeTax, employee.church_tax_rate || 0);
    
    const totalTax = incomeTax + solidaritySurcharge + churchTax;
    
    return {
      taxBase,
      taxableIncome,
      allowances: {
        basic: this.germanConstants.basic_tax_allowance
      },
      taxBrackets: {
        incomeTax,
        solidaritySurcharge,
        churchTax
      },
      credits: {},
      totalTax,
      netTaxBenefit: 0
    };
  }

  /**
   * Calculate other German deductions
   */
  protected async calculateOtherDeductions(
    employee: Employee,
    grossComponents: GrossPayComponents,
    inputData: PayrollInputData
  ): Promise<DeductionCalculation[]> {
    this.log(`Calculating German other deductions`);
    
    const deductions: DeductionCalculation[] = [];
    
    // Advance payments
    if (inputData.advances > 0) {
      deductions.push({
        type: 'OTHER',
        amount: inputData.advances,
        description: 'Advance Payments (Vorschuss)',
        mandatory: true
      });
    }
    
    // Company car benefit (if applicable)
    if (employee.company_car_benefit && employee.company_car_benefit > 0) {
      deductions.push({
        type: 'OTHER',
        amount: employee.company_car_benefit,
        description: 'Company Car Benefit (Dienstwagen)',
        mandatory: false
      });
    }
    
    // Meal subsidies
    if (inputData.otherDeductions?.mealSubsidy && inputData.otherDeductions.mealSubsidy > 0) {
      deductions.push({
        type: 'OTHER',
        amount: inputData.otherDeductions.mealSubsidy,
        description: 'Meal Subsidy (Essenszuschuss)',
        mandatory: false
      });
    }
    
    // Other deductions
    Object.entries(inputData.otherDeductions || {}).forEach(([key, amount]) => {
      if (amount > 0 && key !== 'mealSubsidy') {
        deductions.push({
          type: 'OTHER',
          amount,
          description: key,
          mandatory: false
        });
      }
    });
    
    return deductions;
  }

  /**
   * German-specific validation
   */
  protected async validateCountrySpecificData(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<Record<string, string[]>> {
    const errors: Record<string, string[]> = {};
    
    // Validate contract type
    if (!employee.contract_type || !['VOLLZEIT', 'TEILZEIT', 'MINIJOB', 'MIDIJOB'].includes(employee.contract_type)) {
      errors.contract_type = ['Invalid or missing contract type for German employee'];
    }
    
    // Validate tax class
    const taxClass = employee.tax_class as number;
    if (!taxClass || taxClass < 1 || taxClass > 6) {
      errors.tax_class = ['Tax class must be between 1 and 6 for German employees'];
    }
    
    // Validate Steuer-ID if provided
    if (employee.steuer_id && !this.validateGermanTaxId(employee.steuer_id)) {
      errors.steuer_id = ['Invalid German tax identification number format'];
    }
    
    // Validate Minijob salary limits
    if (employee.contract_type === 'MINIJOB') {
      const monthlyGross = inputData.regularHours * (this.getHourlyRate(employee) || 0);
      if (monthlyGross > this.germanConstants.minijob_threshold) {
        errors.contract_type = [`Minijob salary cannot exceed €${this.germanConstants.minijob_threshold} per month`];
      }
    }
    
    // Validate Midijob salary limits
    if (employee.contract_type === 'MIDIJOB') {
      const monthlyGross = inputData.regularHours * (this.getHourlyRate(employee) || 0);
      if (monthlyGross <= this.germanConstants.minijob_threshold || monthlyGross > this.germanConstants.midijob_threshold) {
        errors.contract_type = [`Midijob salary must be between €${this.germanConstants.minijob_threshold} and €${this.germanConstants.midijob_threshold} per month`];
      }
    }
    
    return errors;
  }

  /**
   * Helper methods - German-specific calculations
   */

  private createDefaultGermanConstants(year: number): GermanPayrollConstants {
    return {
      year,
      country_code: 'DE',
      minimum_wage_hourly: 12.0, // €12.00 as of 2024
      minimum_wage_monthly: 2080, // 40 hours × 4.33 weeks × €12
      basic_tax_allowance: 11784, // 2024 Grundfreibetrag
      tax_rate_14_percent_limit: 11784,
      tax_rate_24_percent_limit: 66760,
      tax_rate_42_percent_limit: 277826,
      tax_rate_45_percent: 0.45,
      pension_rate_employee: 0.093,
      pension_rate_employer: 0.093,
      unemployment_rate_employee: 0.013,
      unemployment_rate_employer: 0.013,
      health_rate_employee: 0.073,
      health_rate_employer: 0.073,
      health_additional_rate_employee: 0.017,
      care_rate_employee: 0.01525,
      care_rate_employer: 0.01525,
      care_additional_rate_childless: 0.006,
      pension_assessment_limit_monthly: 7550,
      health_assessment_limit_monthly: 5175,
      minijob_threshold: 520,
      midijob_threshold: 2000,
      church_tax_rate_8: 0.08,
      church_tax_rate_9: 0.09,
      overtime_premium_rate: 0.25,
      night_work_premium_rate: 0.25,
      sunday_premium_rate: 0.50,
      holiday_premium_rate: 1.00,
      solidarity_surcharge_rate: 0.055,
      solidarity_surcharge_threshold: 1000
    };
  }

  private calculateMinijobInsurance(grossSalary: number): Record<string, InsuranceCalculation> {
    // Minijobs have special insurance rules - mostly employer-paid
    const employerPension = grossSalary * 0.15; // 15% employer pension
    const employerHealth = grossSalary * 0.13; // 13% employer health
    
    return {
      pension: {
        employee: { main: 0 }, // Employee can opt to pay additional 3.6%
        employer: { main: employerPension },
        employeeTotal: 0,
        employerTotal: employerPension
      },
      health: {
        employee: { main: 0 },
        employer: { main: employerHealth },
        employeeTotal: 0,
        employerTotal: employerHealth
      },
      unemployment: {
        employee: { main: 0 },
        employer: { main: 0 }, // No unemployment insurance for minijobs
        employeeTotal: 0,
        employerTotal: 0
      },
      care: {
        employee: { main: 0 },
        employer: { main: 0 }, // No care insurance for minijobs
        employeeTotal: 0,
        employerTotal: 0
      }
    };
  }

  private calculateMidijobInsurance(grossSalary: number): Record<string, InsuranceCalculation> {
    // Midijobs have reduced employee contributions (gleitende Übergangsbereich)
    const reductionFactor = this.calculateMidijobReductionFactor(grossSalary);
    
    const pensionAssessmentBase = Math.min(grossSalary, this.germanConstants.pension_assessment_limit_monthly);
    const healthAssessmentBase = Math.min(grossSalary, this.germanConstants.health_assessment_limit_monthly);
    
    return {
      pension: {
        employee: { main: pensionAssessmentBase * this.germanConstants.pension_rate_employee * reductionFactor },
        employer: { main: pensionAssessmentBase * this.germanConstants.pension_rate_employer },
        employeeTotal: pensionAssessmentBase * this.germanConstants.pension_rate_employee * reductionFactor,
        employerTotal: pensionAssessmentBase * this.germanConstants.pension_rate_employer
      },
      health: {
        employee: { main: healthAssessmentBase * (this.germanConstants.health_rate_employee + this.germanConstants.health_additional_rate_employee) * reductionFactor },
        employer: { main: healthAssessmentBase * this.germanConstants.health_rate_employer },
        employeeTotal: healthAssessmentBase * (this.germanConstants.health_rate_employee + this.germanConstants.health_additional_rate_employee) * reductionFactor,
        employerTotal: healthAssessmentBase * this.germanConstants.health_rate_employer
      },
      unemployment: {
        employee: { main: pensionAssessmentBase * this.germanConstants.unemployment_rate_employee * reductionFactor },
        employer: { main: pensionAssessmentBase * this.germanConstants.unemployment_rate_employer },
        employeeTotal: pensionAssessmentBase * this.germanConstants.unemployment_rate_employee * reductionFactor,
        employerTotal: pensionAssessmentBase * this.germanConstants.unemployment_rate_employer
      },
      care: {
        employee: { main: healthAssessmentBase * this.germanConstants.care_rate_employee * reductionFactor },
        employer: { main: healthAssessmentBase * this.germanConstants.care_rate_employer },
        employeeTotal: healthAssessmentBase * this.germanConstants.care_rate_employee * reductionFactor,
        employerTotal: healthAssessmentBase * this.germanConstants.care_rate_employer
      }
    };
  }

  private calculateStandardInsurance(grossSalary: number): Record<string, InsuranceCalculation> {
    const pensionAssessmentBase = Math.min(grossSalary, this.germanConstants.pension_assessment_limit_monthly);
    const healthAssessmentBase = Math.min(grossSalary, this.germanConstants.health_assessment_limit_monthly);
    
    // Check if employee is childless and over 23 for additional care insurance
    const careAdditionalRate = (this.employee.is_childless && this.calculateAge(this.employee.birth_date || '') > 23) 
      ? this.germanConstants.care_additional_rate_childless 
      : 0;
    
    return {
      pension: {
        employee: { main: pensionAssessmentBase * this.germanConstants.pension_rate_employee },
        employer: { main: pensionAssessmentBase * this.germanConstants.pension_rate_employer },
        employeeTotal: pensionAssessmentBase * this.germanConstants.pension_rate_employee,
        employerTotal: pensionAssessmentBase * this.germanConstants.pension_rate_employer
      },
      health: {
        employee: { 
          main: healthAssessmentBase * this.germanConstants.health_rate_employee,
          additional: healthAssessmentBase * this.germanConstants.health_additional_rate_employee
        },
        employer: { main: healthAssessmentBase * this.germanConstants.health_rate_employer },
        employeeTotal: healthAssessmentBase * (this.germanConstants.health_rate_employee + this.germanConstants.health_additional_rate_employee),
        employerTotal: healthAssessmentBase * this.germanConstants.health_rate_employer
      },
      unemployment: {
        employee: { main: pensionAssessmentBase * this.germanConstants.unemployment_rate_employee },
        employer: { main: pensionAssessmentBase * this.germanConstants.unemployment_rate_employer },
        employeeTotal: pensionAssessmentBase * this.germanConstants.unemployment_rate_employee,
        employerTotal: pensionAssessmentBase * this.germanConstants.unemployment_rate_employer
      },
      care: {
        employee: { 
          main: healthAssessmentBase * this.germanConstants.care_rate_employee,
          additional: healthAssessmentBase * careAdditionalRate
        },
        employer: { main: healthAssessmentBase * this.germanConstants.care_rate_employer },
        employeeTotal: healthAssessmentBase * (this.germanConstants.care_rate_employee + careAdditionalRate),
        employerTotal: healthAssessmentBase * this.germanConstants.care_rate_employer
      }
    };
  }

  private calculateMidijobReductionFactor(grossSalary: number): number {
    // Simplified reduction factor calculation for Midijob
    // Actual formula is more complex and depends on individual circumstances
    const minijobThreshold = this.germanConstants.minijob_threshold;
    const midijobThreshold = this.germanConstants.midijob_threshold;
    
    if (grossSalary <= minijobThreshold) return 0;
    if (grossSalary >= midijobThreshold) return 1;
    
    // Linear interpolation (simplified)
    return (grossSalary - minijobThreshold) / (midijobThreshold - minijobThreshold);
  }

  private calculateGermanIncomeTax(taxableIncome: number, taxClass: GermanTaxClass): number {
    // Simplified German income tax calculation
    // Actual calculation uses the official BMF formulas
    
    if (taxableIncome <= this.germanConstants.basic_tax_allowance) {
      return 0;
    }
    
    let tax = 0;
    let remainingIncome = taxableIncome - this.germanConstants.basic_tax_allowance;
    
    // First bracket (14% rate)
    const firstBracketLimit = this.germanConstants.tax_rate_14_percent_limit - this.germanConstants.basic_tax_allowance;
    if (remainingIncome > 0) {
      const taxableAtThisRate = Math.min(remainingIncome, firstBracketLimit);
      tax += taxableAtThisRate * 0.14;
      remainingIncome -= taxableAtThisRate;
    }
    
    // Additional brackets would be calculated similarly...
    // This is a simplified version for demonstration
    
    // Apply tax class multipliers (simplified)
    const taxClassMultipliers = { 1: 1, 2: 1, 3: 0.6, 4: 1, 5: 1.4, 6: 1.2 };
    tax *= taxClassMultipliers[taxClass] || 1;
    
    return Math.round(tax * 100) / 100; // Round to cents
  }

  private calculateSolidaritySurcharge(incomeTax: number): number {
    if (incomeTax <= this.germanConstants.solidarity_surcharge_threshold) {
      return 0;
    }
    return incomeTax * this.germanConstants.solidarity_surcharge_rate;
  }

  private calculateChurchTax(incomeTax: number, churchTaxRate: number): number {
    if (!churchTaxRate || churchTaxRate === 0) {
      return 0;
    }
    return incomeTax * churchTaxRate;
  }

  private calculateAge(birthDate: string): number {
    if (!birthDate) return 0;
    
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  }

  private validateGermanTaxId(taxId: string): boolean {
    // Basic validation for German tax identification number (11 digits)
    const pattern = /^\d{11}$/;
    return pattern.test(taxId);
  }
}